import { Injectable } from "@angular/core";
import { CanActivateChild } from "@angular/router";
import { Router } from "@angular/router";
import { Observable, Subscription, forkJoin, interval } from "rxjs";
import { AuthenticationService } from "./authentication.service";
import { environment } from "../../../environments/environment";
import { ApiService } from "../services/api.service";
import { map } from "rxjs/operators";
import { RoleModel } from "src/app/shared/models/role.model";
import { PermissionModel } from "src/app/shared/models/permission.model";
import { RoleManagementService } from "./role-management.service";
import { MsalService } from "@azure/msal-angular";

@Injectable()
export class CanActivateChildAuthGuard implements CanActivateChild {
  private allow: Observable<boolean>;
  private _userSub: Subscription;
  private result;
  private tokenExpiration: any;

  constructor(
    private router: Router,
    private apiService: ApiService,
    private authService: AuthenticationService,
    private msalService: MsalService,
    private roleService: RoleManagementService
  ) {}

  canActivateChild(): Observable<boolean> {
    return environment.azsso === "true" ? this.getUser() : this.mockGetUser();
  }

  mockGetUser() {
    if (this.authService.getLoggedInUser()) {
      this.allow = new Observable((observer) => {
        observer.next(true);
        observer.complete();
      });
      return this.allow;
    }
    this.allow = new Observable((observer) => {
      this.userLoginAPI(observer, false);
    });
    return this.allow;
  }

  setUpRolesPermissions(observer) {
    forkJoin([
      this.apiService.get("roles/", ""),
      this.apiService.get("permissions/", ""),
    ])
      .pipe(
        map(([roles, permissions]) => {
          return { roles, permissions };
        })
      )
      .subscribe((res) => {
        this.roleService.setRoles(<RoleModel[]>res.roles);
        this.roleService.setPermissions(<PermissionModel[]>res.permissions);
        observer.next(true);
        observer.complete();
      });
  }

  getUser() {
    const token = this.authService.getTokenHeader();
    if (token === undefined || token === "" || !token) {
      this.allow = null;
    }
    if (this.allow) {
      this.allow = new Observable((observer) => {
        this.fetchLoginToken(observer, false);
        observer.next(true);
        observer.complete();
      });
      return this.allow;
    }

    this.allow = new Observable((observer) => {
      if (this._userSub) {
        this._userSub.unsubscribe();
      }
      if (this.msalService.instance.getAllAccounts().length > 0) {
        this.fetchLoginToken(observer, true);
      } else {
        this.startLoginAzure(observer);
      }
    });
    return this.allow;
  }

  public async startLoginAzure(observer) {
    if (
      this.msalService.instance != null &&
      this.msalService.instance["browserStorage"] != null
    ) {
      this.msalService.instance[
        "browserStorage"
      ].cleanRequestByInteractionType();
    }
    if (this.msalService.instance.initialize) {
      await this.msalService.instance.initialize();
    }
    const loginRequest = {
      scopes: ["user.read"],
    };
    const interactionInProgress =
      sessionStorage.getItem("msal.interaction.status") !== null;
    if (interactionInProgress) {
      sessionStorage.removeItem("msal.interaction.status");
    }
   
    this.msalService.loginPopup(loginRequest).subscribe(
      (result) => {
        this.authService.setTokenHeader(result.idToken);
        this.tokenExpiration = result.expiresOn;
        this.userLoginAPI(observer, true);
      },
      (err) => {
      }
    );
  }

  private userLoginAPI(observer, saveMsal) {
    this._userSub = this.apiService.get("login/", "").subscribe(
      (user) => {
        interval(15000).subscribe(() => {
          if (this.authService.checkTokenValidity() === true) {
            this.getMaintenanceWindow();
          }
        });
        this.authService.setUserLoggedIn(user);
        if (saveMsal) {
          this.authService.addExpirationDate(this.tokenExpiration);
        } else {
          this.authService.addExpirationDate(new Date());
        }
        if (
          !this.roleService.getRoles().length &&
          !this.roleService.getSuitePermissions()
        ) {
          this.setUpRolesPermissions(observer);
        } else {
          observer.next(true);
          observer.complete();
        }
      },
      (err) => {
        if (err.status === 401) {
          this.authService.clearMsalSession();
          this.msalService.loginRedirect({
            scopes: ["user.read"],
          });
        } else if (err.status === 500) {
          this.router.navigate(["/access-denied"]);
        } else {
          this.authService.loggedInUser = err.error.userEmailId;
        }
      }
    );
  }

  private getMaintenanceWindow() {
    this.apiService
      .get("IsClientUnderMaintenance/", "")
      .subscribe((response) => {
        this.result = response;
        if (this.result) {
          if (
            this.router.url.endsWith("/") ||
            this.router.url.endsWith("under-maintenance")
          ) {
            this.authService.redirectToUnderMaintenance();
          } else {
            alert("The application is going under maintenance.");
            this.authService.redirectToUnderMaintenance();
          }
        } else {
          if (this.router.url.endsWith("under-maintenance")) {
            this.router.navigate(["/home"]);
          }
        }
      });
  }

  private deniedAccess(observer: any, error: any) {
    this.allow = null;
    const errorMessage =
      error && error.error && error.error.error
        ? error.error.error
        : "Something went wrong";
    if (errorMessage === "Authentication Token Expired") {
      this.router.navigate(["/access-denied"]);
    } else if (error.status && error.status === 401) {
      this.authService.accessNotAllowed();
      this.authService.logoutUser();
    } else {
      this.authService.serverError();
    }
    observer.next(false);
    observer.complete();
  }

  public async fetchLoginToken(observer: any, isInitialLogin: boolean) {
    const accessTokenRequest = {
      scopes: ["user.read"],
      account: this.msalService.instance.getAllAccounts()[0],
    };
    if (this.msalService.instance.initialize) {
      await this.msalService.instance.initialize();
    }
    this.msalService.acquireTokenSilent(accessTokenRequest).subscribe(
      (result) => {
        this.authService.setTokenHeader(result.idToken);
        this.tokenExpiration = result.expiresOn;
        if (isInitialLogin) this.userLoginAPI(observer, true);
        else this.authService.addExpirationDate(this.tokenExpiration);
      },
      (err) => {
        this.startLoginAzure(observer);
      }
    );
  }
}